/**
 * RidePrimitive — dependency-free pseudo-3D placeholder for a garage ride.
 * The garage has no three.js / r3f / model-viewer dependency and no .glb assets,
 * so the old "3D" view was a remote Unsplash <img> keyed by vehicle_type; unknown
 * or new types rendered nothing, leaving a black void. This draws a distinct
 * inline SVG primitive per vehicle type, tinted with the ride neon color and
 * rotatable via CSS 3D transforms.
 */

const SHAPES = {
    twoWheelBike: { wheels: [[26, 66, 16], [94, 66, 16]], body: 'M26 66 L52 34 L78 34 L94 66 M52 34 L60 66 M78 34 L86 50' },
    fatWheelBike: { wheels: [[28, 64, 20], [92, 64, 20]], body: 'M28 64 L50 30 L80 30 L92 64 M50 30 L58 64 M80 30 L88 46' },
    scooterStand: { wheels: [[24, 74, 12], [98, 74, 12]], body: 'M24 74 L24 26 L46 26 M24 74 L98 74 M92 74 L92 56' },
    board: { wheels: [[34, 78, 8], [86, 78, 8]], body: 'M18 66 Q60 52 102 66 L102 70 Q60 58 18 70 Z' },
    singleWheel: { wheels: [[60, 66, 26]], body: 'M30 60 L90 60 M34 52 L86 52' },
    quadFrame: { wheels: [[26, 74, 14], [94, 74, 14], [34, 52, 10], [86, 52, 10]], body: 'M26 74 L44 40 L80 40 L94 74 Z' },
    kartFrame: { wheels: [[28, 76, 12], [96, 76, 12]], body: 'M18 68 L34 44 L88 44 L106 68 Z M50 44 L50 30 L74 30 L74 44' },
    chair: { wheels: [[44, 74, 20], [88, 80, 10]], body: 'M30 56 L58 56 L58 24 L46 24 M58 56 L88 62' },
    paddle: { wheels: [], body: 'M14 74 Q60 56 106 74 Q60 84 14 74 Z M70 66 L86 20 M78 20 L94 20' },
    foil: { wheels: [], body: 'M18 40 Q60 30 102 40 Q60 50 18 40 Z M60 44 L60 74 M36 78 L84 78' },
    diy: { wheels: [[28, 68, 15], [94, 68, 15]], body: 'M28 68 L48 38 L82 44 L94 68 M48 38 L62 68 M60 30 L74 30 L74 46 L60 46 Z' },
};

const MAP = {
    ebike: 'twoWheelBike', bicycle: 'twoWheelBike', mtb: 'fatWheelBike',
    escooter: 'scooterStand', scooter: 'scooterStand',
    skateboard: 'board', electric_skateboard: 'board',
    onewheel: 'singleWheel', euc: 'singleWheel',
    dirt_bike: 'fatWheelBike', dirtbike: 'fatWheelBike', electric_dirt_bike: 'fatWheelBike',
    emoto: 'twoWheelBike', quad: 'quadFrame', kart: 'kartFrame',
    wheelchair: 'chair', diy_conversion: 'diy',
    paddleboard: 'paddle', foilboard: 'foil', custom: 'twoWheelBike',
};

export default function RidePrimitive({ vehicleType, neon = '#FF2020', rotation = 0, label }) {
    const shape = SHAPES[MAP[vehicleType] || 'twoWheelBike'];
    return (
        <div className="flex flex-col items-center" data-testid="ride-primitive"
            data-vehicle-type={vehicleType} style={{ perspective: '1200px' }}>
            <svg viewBox="0 0 120 100" width="230" height="192" role="img"
                aria-label={label || vehicleType || 'ride'}
                style={{
                    transform: `rotateY(${rotation}deg)`,
                    transition: 'transform 0.5s',
                    filter: `drop-shadow(0 0 18px ${neon}88)`,
                }}>
                <ellipse cx="60" cy="92" rx="46" ry="5" fill={neon} opacity="0.18" />
                {shape.wheels.map(([cx, cy, r], i) => (
                    <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={neon} strokeWidth="4" opacity="0.95" />
                ))}
                <path d={shape.body} fill="none" stroke={neon} strokeWidth="4"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </div>
    );
}
